import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo
} from 'react'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import {
  Collapsible,
  DisplayText,
  TextField,
  TextStyle,
  Button,
  ButtonGroup,
  Badge,
  Card,
  Banner,
  Link
} from '@superops/ui-design-system'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  AppContext,
  B1,
  DisabledTag,
  getTranslatedText,
  Loader,
  toast,
  COMMON_CONSTANTS,
  ApiProgressLoader,
  useActivityTracker
} from '@nile/components'
import { SVGIcon } from '@superops/super-icons'
import PricingPageContext from '@nile/components/src/Pages/Pricing/context/PricingPageContext'
import {
  AccountClient,
  UPGRADE_SUBSCRIPTION,
  UPGRADE_SUBSCRIPTION_STATUS,
  CREATE_PURCHASE_SUBSCRIPTION_CONFIGURATION,
  GET_ESTIMATE_FOR_LINEITEM,
  GET_ESTIMATE_FOR_ALL_LINEITEMS,
  GET_ESTIMATE_FOR_LINEITEM_UPGRADE
} from '@nile/graphql'
import dayjs from 'dayjs'
import { isEmpty, debounce } from 'lodash'
import { useFlags } from 'launchdarkly-react-client-sdk'
import FeatureLineItem from '@nile/components/src/Pages/Pricing/PricingPageContainer/FeatureLineItem'
import { ContentWrapper } from '@nile/components/src/Pages/Pricing/PricingPageContainer/styles'

const ContainerWrapper = styled.div`
  .dotted-line {
    border-bottom: 2px dashed var(--sops-divider);
  }
  .cascadeContainer {
    &:first-child {
      background: unset !important;
      overflow: visible;
      width: 50%;
    }
    &:last-child {
      /* padding: 0px 50px; */
    }
  }
`

const ItemGroupList = (props) => {
  const {
    nextTermInvoice = false,
    deviceCount,
    invoiceState,
    cumulatedInvoice,
    openSummary,
    currency,
    locale,
    tenure,
    isAnnual,
    nextinvoiceState,
    isOverageEstimate
  } = props
  const [collapseStatus, setCollapseStatus] = useState(openSummary || false)

  const { dateFrom, dateTo, isProrated, plan, charge } = invoiceState

  useEffect(() => {
    setCollapseStatus(openSummary)
  }, [openSummary])

  const isMonthlyPayment =
    ((isProrated && tenure === 'MONTHLY') ||
      (!isProrated && tenure === 'MONTHLY')) &&
    nextinvoiceState

  const startDate = dayjs(dateFrom)
  const endDate = dayjs(dateTo)

  const totalMonths = endDate.diff(startDate, 'month')

  // calculating the remaining days & month
  const adjustedStartDate = startDate.add(totalMonths, 'month')
  const remainingDays = endDate.diff(adjustedStartDate, 'day')

  return (
    <div className='w100 timer-bg rounded-2xl p20'>
      <div className='fx g5 actr'>
        <SVGIcon name='About' />
        {isProrated ? (
          <DisplayText size='large'>
            Prorated charges for{' '}
            <TextStyle weight='Bold'>{plan?.quantity} devices </TextStyle>
          </DisplayText>
        ) : isOverageEstimate ? (
          <DisplayText size='large'>
            Overage charges for{' '}
            <TextStyle weight='Bold'>{charge?.quantity} devices </TextStyle>
          </DisplayText>
        ) : isAnnual ? (
          <DisplayText size='large'>
            Annual charges for{' '}
            <TextStyle weight='Bold'>{plan?.quantity} devices </TextStyle>
          </DisplayText>
        ) : (
          <DisplayText size='large'>
            {startDate.format('MMMM YYYY')} billing charges for{' '}
            <TextStyle weight='Bold'>{plan?.quantity} device </TextStyle> is{' '}
            <TextStyle weight='Bold'>
              {locale &&
                currency &&
                tenure &&
                new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: 2,
                  currencyDisplay: 'narrowSymbol'
                }).format(isAnnual ? plan?.amount : cumulatedInvoice?.total)}
            </TextStyle>
          </DisplayText>
        )}
      </div>
      {/* <div className="dotted-line mt20 mb20" /> */}
      {!collapseStatus ? (
        <div className='mt20 mb5'>
          <Button onClick={() => setCollapseStatus(!collapseStatus)} plain>
            <div className='fx actr g5'>
              View Summary
              <div
                className={`fx actr accordion-transition ${
                  collapseStatus ? 'rotate-180' : ''
                }`}
              >
                <SVGIcon name='ArrowDown' />
              </div>
            </div>
          </Button>
        </div>
      ) : (
        <div className='dotted-line mt20 mb20' />
      )}

      <Collapsible
        open={collapseStatus}
        transition={{ duration: '150ms', timingFunction: 'ease' }}
      >
        <div className='accordion-transition'>
          <TextStyle variation='subdued'>
            For{' '}
            {!isProrated &&
              !isOverageEstimate &&
              remainingDays > 0 &&
              `${remainingDays} ${remainingDays == 1 ? 'Day' : 'Days'}`}{' '}
            {totalMonths > 0 &&
              `${totalMonths} ${totalMonths == 1 ? 'Month' : 'Months'} `}
            ( {startDate.format('DD/MM/YYYY')} to {endDate.format('DD/MM/YYYY')}{' '}
            ){' '}
          </TextStyle>

          <div className='mt10'>
            {invoiceState?.plan?.tieredBreakdown?.map((tier) => {
              return (
                <div className='mt5'>
                  <DisplayText size='large' weight='Medium'>
                    <div className='fx '>
                      <span className='w30'>
                        {/* {tier.startUnit} {tier.endUnit && `- ${tier.endUnit}`}{' '} */}
                        {tier.startUnit && tier.endUnit
                          ? `${tier.startUnit} - ${tier.endUnit}`
                          : `More than ${tier.startUnit} `}
                      </span>
                      <span className='w30 ta-r'>
                        {tier?.quantity} x{' '}
                        {locale &&
                          currency &&
                          tenure &&
                          new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: 0,
                            currencyDisplay: 'narrowSymbol'
                          }).format(tier?.unitPrice)}
                      </span>
                      <span className='w40 tend'>
                        <TextStyle weight='Bold'>
                          {locale &&
                            currency &&
                            tenure &&
                            new Intl.NumberFormat(locale, {
                              style: 'currency',
                              currency,
                              minimumFractionDigits: 2,
                              currencyDisplay: 'narrowSymbol'
                            }).format(tier?.amount)}
                        </TextStyle>
                      </span>
                    </div>
                  </DisplayText>
                </div>
              )
            })}
          </div>
          {isOverageEstimate && invoiceState?.charge && (
            <div className='mt10'>
              <div className='mt5'>
                <DisplayText size='large' weight='Medium'>
                  <div className='fx '>
                    <span className='w30'>
                      <Badge status='attention'>
                        <FormattedMessage id='itsm.pricing.overage.entity.label' />
                      </Badge>
                    </span>
                    <span className='w30 ta-r'>
                      {invoiceState?.charge?.quantity} x{' '}
                      {locale &&
                        currency &&
                        tenure &&
                        new Intl.NumberFormat(locale, {
                          style: 'currency',
                          currency,
                          minimumFractionDigits: 0,
                          currencyDisplay: 'narrowSymbol'
                        }).format(invoiceState?.charge?.unitPrice)}
                    </span>
                    <span className='w40 tend'>
                      <TextStyle weight='Bold'>
                        {locale &&
                          currency &&
                          tenure &&
                          new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: 2,
                            currencyDisplay: 'narrowSymbol'
                          }).format(invoiceState?.charge?.amount)}
                      </TextStyle>
                    </span>
                  </div>
                </DisplayText>
              </div>
            </div>
          )}

          <div className='dotted-line mt20 mb20' />
          {nextTermInvoice && invoiceState?.charge && (
            <div>
              <div className='mt5'>
                <DisplayText size='large' weight='Medium'>
                  <div className='fx '>
                    <span className='w30'>
                      <Badge status='attention'>
                        {' '}
                        <FormattedMessage id='itsm.pricing.overage.entity.label' />
                      </Badge>
                    </span>
                    <span className='w30 ta-r'>
                      {invoiceState?.charge?.quantity} x{' '}
                      {locale &&
                        currency &&
                        tenure &&
                        new Intl.NumberFormat(locale, {
                          style: 'currency',
                          currency,
                          minimumFractionDigits: 0,
                          currencyDisplay: 'narrowSymbol'
                        }).format(invoiceState?.charge?.unitPrice)}
                    </span>
                    <span className='w40 tend'>
                      <TextStyle weight='Bold'>
                        {locale &&
                          currency &&
                          tenure &&
                          new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: 2,
                            currencyDisplay: 'narrowSymbol'
                          }).format(invoiceState?.charge?.amount)}
                      </TextStyle>
                    </span>
                  </div>
                </DisplayText>
              </div>

              <div className='dotted-line mt20 mb20' />
            </div>
          )}

          <div className='mb10'>
            <DisplayText size='large' weight='Medium'>
              <div className='fx '>
                <span className='w60 ta-r'>
                  <FormattedMessage
                    id={
                      isAnnual
                        ? 'itsm.pricing.subtotal.label'
                        : 'invoice.details.total'
                    }
                  />
                </span>
                <span className='w40 tend'>
                  <TextStyle weight='Bold'>
                    {locale &&
                      currency &&
                      tenure &&
                      new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency,
                        minimumFractionDigits: 2,
                        currencyDisplay: 'narrowSymbol'
                      }).format(
                        isAnnual
                          ? invoiceState?.plan?.amount
                          : cumulatedInvoice?.subTotal
                      )}
                  </TextStyle>
                </span>
              </div>
            </DisplayText>
          </div>

          {!isAnnual && cumulatedInvoice?.roundOff && (
            <div className='mb-5'>
              <DisplayText size='large' weight='Medium'>
                <div className='fx '>
                  <span className='w60 ta-r'>
                    <TextStyle variation='subdued'>Round Off</TextStyle>
                  </span>
                  <span className='w40 tend'>
                    <TextStyle variation='subdued'>
                      {locale &&
                        currency &&
                        tenure &&
                        new Intl.NumberFormat(locale, {
                          style: 'currency',
                          currency,
                          minimumFractionDigits: 2,
                          currencyDisplay: 'narrowSymbol'
                        }).format(cumulatedInvoice?.roundOff)}
                    </TextStyle>
                  </span>
                </div>
              </DisplayText>
            </div>
          )}

          {(nextTermInvoice || isOverageEstimate) &&
            cumulatedInvoice?.creditsApplied && (
              <div className='mb10'>
                <DisplayText size='large' weight='Medium'>
                  <div className='fx '>
                    <span className='w60 ta-r'>Credits Applied</span>
                    <span className='w40 tend'>
                      <TextStyle variation='subdued'>
                        {locale &&
                          currency &&
                          tenure &&
                          new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: 2,
                            currencyDisplay: 'narrowSymbol'
                          }).format(cumulatedInvoice?.creditsApplied)}
                      </TextStyle>
                    </span>
                  </div>
                </DisplayText>
              </div>
            )}

          {((isMonthlyPayment && !isOverageEstimate) ||
            nextTermInvoice ||
            isOverageEstimate) && (
            <div className=''>
              <DisplayText size='large' weight='Medium'>
                <div className='fx '>
                  <span className='w60 ta-r'>
                    <TextStyle weight='Bold' variation='positive'>
                      <FormattedMessage
                        id={
                          nextTermInvoice || isOverageEstimate
                            ? 'attribute.totalAmount'
                            : 'invoice.details.payNow'
                        }
                      />
                    </TextStyle>
                  </span>
                  <span className='w40 tend'>
                    <TextStyle weight='Bold' variation='positive'>
                      {locale &&
                        currency &&
                        tenure &&
                        new Intl.NumberFormat(locale, {
                          style: 'currency',
                          currency,
                          minimumFractionDigits: 2,
                          currencyDisplay: 'narrowSymbol'
                        }).format(cumulatedInvoice?.amountDue)}
                    </TextStyle>
                  </span>
                </div>
              </DisplayText>
            </div>
          )}
        </div>
      </Collapsible>
    </div>
  )
}

const getFeatureList = (el, selectedPlan) => {
  return (
    <>
      {el?.features?.length > 0 && (
        <FeatureLineItem
          features={el?.features}
          labelKey={el?.labelKey}
          detailCard
          plan={selectedPlan}
        />
      )}
      {el?.subcategories &&
        el?.subcategories?.map((subel) => getFeatureList(subel))}
    </>
  )
}

const ITPricingEstimateContainer = (props) => {
  const {
    selectedPlan,
    tenure,
    setTenure,
    setSelectedPlan,
    currency,
    setpageLoader,
    locale,
    isUpgrade,
    setOpenOverageAssetModal,
    openOverageAssetModal,
    isTieredModel,
    subscriptionTypes,
    allFeatures,
    planChange
  } = props
  const intl = useIntl()
  const flags = useFlags()
  const pricingContext = useContext(PricingPageContext)
  const appContext = useContext(AppContext)
  const isPrimePlus = selectedPlan?.chargeBeePlanId === 'Prime_Plus'
  const initialValue = useMemo(() => {
    const { pricingModel, price } = selectedPlan?.planPrices?.[0]
    const minVal = price?.[pricingModel]?.[0]?.endUnit
    return minVal
  }, [selectedPlan])

  const DEFAULT_DEVICE_COUNT = initialValue ?? (isPrimePlus ? 150 : 100)
  const { user, getUser } = appContext
  const [deviceCount, setDeviceCount] = useState('NA')
  const [minimumDeviceCount, setMinimumDeviceCount] = useState(
    `${DEFAULT_DEVICE_COUNT}`
  )
  const [updatedDeviceCount, setupdatedDeviceCount] = useState()
  const [overAgeDevices, setOverAgeDevices] = useState('NA')
  const [countErrors, setCountErrors] = useState({})

  const [planEstimate, setPlanEstimate] = useState({})
  const [changeTimeout, setChangeTimeout] = useState(null)
  const [isEstimating, setIsEstimating] = useState(false)
  const [intitalEstimateLoading, setIntitalEstimateLoading] = useState(false)
  const [invoiceState, setInvoiceState] = useState({})
  const [overageEstimate, setOverageEstimate] = useState({})
  const [nextinvoiceState, setNextInvoiceState] = useState({})

  const newPlanFeaturesList = useMemo(() => {
    let planType
    const matchingSubscription = subscriptionTypes
      ? Object.values(subscriptionTypes).find(
          (el) => el?.chargeBeePlanId === selectedPlan?.chargeBeePlanId
        )
      : null
    if (matchingSubscription) {
      planType = matchingSubscription.planType

      const processedFeaturesList = Object.entries(allFeatures).map(
        ([featureTitle, features]) => {
          const title = featureTitle.split('_').join(' ')
          const featuresList = features?.filter((feat) => feat[planType])

          return featuresList?.length
            ? { labelKey: title, features: featuresList }
            : null
        }
      )

      return processedFeaturesList
    } else {
      return []
    }
  }, [allFeatures, selectedPlan, subscriptionTypes])

  const getUpdatedUser = async () => {
    await getUser()
  }

  const openCheckout = () => {
    const chargebeeInstance = window.Chargebee.getInstance()
    chargebeeInstance.openCheckout({
      hostedPage: function () {
        return AccountClient.mutate({
          mutation: isUpgrade
            ? UPGRADE_SUBSCRIPTION
            : CREATE_PURCHASE_SUBSCRIPTION_CONFIGURATION,
          variables: {
            input: {
              chargeBeePlanPriceId:
                planEstimate?.lineItemGroups[0]?.plan?.planPrice
                  ?.chargeBeePlanPriceId,
              addons: [],
              quantity: parseInt(deviceCount)
            }
          }
        }).then((res) => {
          if (isUpgrade) {
            if (res?.data?.upgradeSubscription) {
              return res.data.upgradeSubscription
            }
          } else {
            if (res?.data?.createPurchaseSubscriptionConfiguration) {
              useActivityTracker({
                event: 'IT Inproduct purchase',
                data: {
                  source: 'C. proceed to payment btn new buy',
                  subscription:
                    planEstimate?.lineItemGroups[0]?.plan?.planPrice
                      ?.chargeBeePlanPriceId,
                  quantity: planEstimate?.lineItemGroups[0]?.plan.quantity
                }
              })
              return res.data.createPurchaseSubscriptionConfiguration
            }
          }
        })
      },
      success: function () {
        AccountClient.mutate({
          mutation: UPGRADE_SUBSCRIPTION_STATUS,
          variables: {
            input: {
              status: 'SUCCESS'
            }
          }
        }).then((res) => {
          if (res?.data?.updateSubscriptionPurchaseStatus) {
            useActivityTracker({
              event: 'IT Inproduct purchase',
              data: {
                source: 'D. new payment conformation',
                subscription:
                  planEstimate?.lineItemGroups[0]?.plan?.planPrice
                    ?.chargeBeePlanPriceId,
                quantity: planEstimate?.lineItemGroups[0]?.plan.quantity
              }
            })
            getUpdatedUser()
            toast('success', 'Payment successful')
            chargebeeInstance.closeAll()
            window.location.reload()
          } else {
            useActivityTracker({
              event: 'Inproduct purchase',
              data: {
                source: 'Purchase plan payment inprogress',
                subscription:
                  planEstimate?.lineItemGroups[0]?.plan?.planPrice
                    ?.chargeBeePlanPriceId,
                quantity: planEstimate?.lineItemGroups[0]?.plan.quantity
              }
            })
            chargebeeInstance.closeAll()
            window.location.reload()
          }
        })
      },
      close: function () {
        AccountClient.mutate({
          mutation: UPGRADE_SUBSCRIPTION_STATUS,
          variables: {
            input: {
              status: 'CANCEL'
            }
          }
        }).then((res) => {
          if (res?.data?.updateSubscriptionPurchaseStatus) {
            useActivityTracker({
              event: 'Inproduct purchase',
              data: {
                source: 'E. Payment cancelled',
                subscription:
                  planEstimate?.lineItemGroups[0].plan?.planPrice
                    ?.chargeBeePlanPriceId,
                quantity: planEstimate?.lineItemGroups[0]?.plan.quantity
              }
            })
          }
          chargebeeInstance.closeAll()
        })
      }
    })
  }

  const setcount = (count) => {
    setIsEstimating(true)
    setupdatedDeviceCount(count)
  }
  const updateDeviceCount = useCallback(debounce(setcount, 1000), [])

  const getEstimatedPriceForLineItem = (data = null, quantity = null) => {
    setIsEstimating(true)
    const estimateData = data || planEstimate
    const quantityData = quantity
    AccountClient.query({
      query: GET_ESTIMATE_FOR_ALL_LINEITEMS,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          plan: {
            planPrice: {
              id: estimateData?.lineItemGroups?.[0].plan?.planPrice?.id
            },
            quantity: quantityData
          },
          addons: []
        }
      }
    })
      .then((res) => {
        if (res?.data?.getProductSubscriptionEstimateForLineItems) {
          const result = res.data.getProductSubscriptionEstimateForLineItems
          setInvoiceState(result?.currentInvoiceEstimate)
          setNextInvoiceState(result?.nextInvoiceEstimate)
          setPlanEstimate(
            result?.currentInvoiceEstimate ?? result?.nextInvoiceEstimate
          )
          if (result?.overageInvoiceEstimate) {
            setOverageEstimate(result?.overageInvoiceEstimate)
          } else {
            setOverageEstimate()
          }
          if (isUpgrade) {
            const { billableOverage } = result?.offeringEntities.find(
              (entity) => entity.offeringEntityType === 'ALL_ASSET'
            )
            setOverAgeDevices(billableOverage ? String(billableOverage) : 'NA')
          }
        }
      })
      .finally(() => setIsEstimating(false))
  }

  const getEstimateForExistingLineItem = (tenureChange = false) => {
    if (tenureChange) {
      setIsEstimating(true)
    } else {
      setIntitalEstimateLoading(true)
    }
    const planPriceId = selectedPlan?.planPrices?.find(
      (prPlan) =>
        prPlan?.currency?.currencyCode === currency &&
        prPlan?.duration === tenure &&
        prPlan?.durationValue === 1
    )

    const shouldIncludePlanId = planChange || !(isUpgrade && !tenureChange)
    const planIP = shouldIncludePlanId ? { input: { id: planPriceId?.id } } : {}

    AccountClient.query({
      query: GET_ESTIMATE_FOR_LINEITEM,
      fetchPolicy: 'network-only',
      variables: planIP
    })
      .then((res) => {
        if (res?.data?.getProductSubscriptionEstimateForExistingLineItems) {
          const result =
            res.data.getProductSubscriptionEstimateForExistingLineItems
          // new change Use 'nextInvoiceEstimate' instead termInvoiceEstimate:
          const termResult =
            result?.currentInvoiceEstimate ?? result?.nextInvoiceEstimate
          // new change Use 'currentInvoiceEstimate' instead immediateInvoiceEstimate:
          setInvoiceState(result?.currentInvoiceEstimate)
          setNextInvoiceState(result?.nextInvoiceEstimate)
          setPlanEstimate(
            result?.currentInvoiceEstimate ?? result?.nextInvoiceEstimate
          )
          if (result?.overageInvoiceEstimate) {
            setOverageEstimate(result?.overageInvoiceEstimate)
          } else {
            setOverageEstimate()
          }
          if (isUpgrade && !tenureChange) {
            setPlanEstimate(result?.nextInvoiceEstimate)
            const {
              purchased,
              billableOverage
            } = result?.offeringEntities.find(
              (entity) => entity.offeringEntityType === 'ALL_ASSET'
            )
            setOverAgeDevices(billableOverage ? String(billableOverage) : 'NA')

            let computedMinimumDeviceCount = DEFAULT_DEVICE_COUNT

            if (purchased) {
              if (purchased >= DEFAULT_DEVICE_COUNT) {
                computedMinimumDeviceCount = purchased
              }
              setDeviceCount(String(computedMinimumDeviceCount))
            }

            setMinimumDeviceCount(computedMinimumDeviceCount)
          } else {
            if (deviceCount == 'NA')
              setDeviceCount(String(DEFAULT_DEVICE_COUNT))
          }
          if (tenureChange) {
            getEstimatedPriceForLineItem(termResult, parseInt(deviceCount))
          } else {
            setIsEstimating(false)
          }
        }
      })
      .finally(() => {
        if (!tenureChange) {
          setIntitalEstimateLoading(false)
          setIsEstimating(false)
        }
      })
  }

  useEffect(() => {
    if (!isEmpty(planEstimate) && !intitalEstimateLoading) {
      setChangeTimeout(
        setTimeout(function () {
          getEstimatedPriceForLineItem(null, updatedDeviceCount)
        }, 1000)
      )
      if (changeTimeout) {
        clearTimeout(changeTimeout)
      }
    }
  }, [updatedDeviceCount])

  useEffect(() => {
    if (!isEmpty(planEstimate) && !intitalEstimateLoading) {
      setChangeTimeout(
        setTimeout(function () {
          getEstimateForExistingLineItem(true)
        }, 200)
      )
      if (changeTimeout) {
        clearTimeout(changeTimeout)
      }
    }
  }, [tenure])

  useEffect(() => {
    if (setpageLoader) setpageLoader(isEstimating || intitalEstimateLoading)
  }, [isEstimating, intitalEstimateLoading])

  useEffect(() => {
    getEstimateForExistingLineItem()
  }, [])

  return (
    <ContainerWrapper>
      <div
        className={`flex fh w100 ${
          !(!!flags?.itOverageBilling && !!isUpgrade) ? 'inner-wrapper' : ''
        }`}
      >
        <div className='col g25 cascadeContainer w50'>
          <div className='fx col g5 overflow-hidden feature-card-wrapper'>
            <DisplayText weight='SemiBold' size='extraLarge'>
              {getTranslatedText(
                intl,
                isUpgrade
                  ? 'itsm.pricing.committed.device.count.label'
                  : 'attribute.totalDeviceCount'
              )}
            </DisplayText>
            <div className='fx g5'>
              <TextField
                id='deviceNamecount'
                name='deviceNameCount'
                onChange={(count) => {
                  // setIsEstimating(true);
                  setDeviceCount(count)
                  const d_count = parseInt(count)
                  if (
                    d_count < DEFAULT_DEVICE_COUNT ||
                    d_count < minimumDeviceCount
                  ) {
                    setCountErrors({ name: 'Device count is less' })
                  } else if (!d_count) {
                    setCountErrors({ name: 'Enter minimum device count' })
                  } else {
                    setCountErrors({})
                    updateDeviceCount(count)
                  }
                }}
                disabled={isEstimating || intitalEstimateLoading}
                onBlur={() => {
                  // const d_count = parseInt(deviceCount);
                  // if (d_count < 100) {
                  //   setCountErrors({ name: 'Device count is less' });
                  // } else {
                  //   setCountErrors({});
                  // }
                }}
                placeholder='Enter device count'
                value={deviceCount}
                type='number'
                variant='outline'
                size='small'
                fontSize='fontMedium'
                error={countErrors?.name && <B1>{countErrors?.name}</B1>}
                multiline
                autoComplete='off'
              />
              <div className='fx actr'>
                {isEstimating && <ApiProgressLoader isLoading />}
                {updatedDeviceCount && !isEstimating && (
                  <ApiProgressLoader isSuccess />
                )}
              </div>
            </div>
            <TextStyle variation='subdued'>
              *
              {initialValue > 100
                ? getTranslatedText(
                    intl,
                    'itsm.pricing.minimum.count.note.prime.plus'
                  )
                : getTranslatedText(intl, 'itsm.pricing.minimum.count.note')}
            </TextStyle>

            {newPlanFeaturesList.length ? (
              <ContentWrapper
                className='featureCard scroll full-height-vh'
                scaleHeight
              >
                {newPlanFeaturesList?.map((el) =>
                  getFeatureList(el, selectedPlan)
                )}
              </ContentWrapper>
            ) : null}
          </div>
          {flags?.itOverageBilling && isUpgrade && (
            <div className='fx col g5'>
              <DisplayText weight='SemiBold' size='extraLarge'>
                {getTranslatedText(
                  intl,
                  'itsm.pricing.overage.device.count.label'
                )}
              </DisplayText>
              <TextField
                id='overageDeviceCount'
                name='overageDeviceCount'
                disabled
                placeholder='Overage Device count'
                value={overAgeDevices}
                type='number'
                variant='outline'
                size='small'
                fontSize='fontMedium'
                multiline
                autoComplete='off'
              />
              <div className={overAgeDevices === 'NA' ? 'disabled' : ''}>
                <div className='inner'>
                  <Link
                    onClick={() => {
                      setOpenOverageAssetModal(!openOverageAssetModal)
                    }}
                  >
                    <TextStyle>
                      <FormattedMessage id='itsm.app.pricing.view.devices.label' />
                    </TextStyle>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className={`full-width fx f12 fh w100 cascadeContainer col g40 tw-p-4 ${
            isEstimating ? 'disabled pointer-events-none' : ''
          }`}
        >
          {intitalEstimateLoading ? (
            <div className='fx fh fw min-height-300 actr'>
              <Loader />
            </div>
          ) : (
            <>
              {invoiceState?.lineItemGroups?.map((invoiceSplit, i) => {
                return (
                  <ItemGroupList
                    deviceCount={deviceCount}
                    isAnnual={invoiceState?.lineItemGroups?.length > 1}
                    invoiceState={invoiceSplit}
                    cumulatedInvoice={invoiceState}
                    nextinvoiceState={nextinvoiceState}
                    openSummary={true}
                    {...props}
                  />
                )
              })}
              {invoiceState && tenure == 'YEARLY' && (
                <div className='plr-20'>
                  <div className='mb-5'>
                    <DisplayText size='large' weight='Medium'>
                      <div className='fx '>
                        <span className='w60 ta-r'>
                          <TextStyle weight='Bold'>
                            <FormattedMessage id='invoice.details.total' />
                          </TextStyle>
                        </span>
                        <span className='w40 tend'>
                          <TextStyle weight='Bold'>
                            {locale &&
                              currency &&
                              tenure &&
                              new Intl.NumberFormat(locale, {
                                style: 'currency',
                                currency,
                                minimumFractionDigits: 2,
                                currencyDisplay: 'narrowSymbol'
                              }).format(invoiceState?.subTotal)}
                          </TextStyle>
                        </span>
                      </div>
                    </DisplayText>
                  </div>
                  {invoiceState?.roundOff && (
                    <div className='mb-5'>
                      <DisplayText size='large' weight='Medium'>
                        <div className='fx '>
                          <span className='w60 ta-r'>
                            <TextStyle variation='subdued'>Round Off</TextStyle>
                          </span>
                          <span className='w40 tend'>
                            <TextStyle variation='subdued'>
                              {locale &&
                                currency &&
                                tenure &&
                                new Intl.NumberFormat(locale, {
                                  style: 'currency',
                                  currency,
                                  minimumFractionDigits: 2,
                                  currencyDisplay: 'narrowSymbol'
                                }).format(invoiceState.roundOff)}
                            </TextStyle>
                          </span>
                        </div>
                      </DisplayText>
                    </div>
                  )}
                  {invoiceState?.creditsApplied && (
                    <div className='mb-5'>
                      <DisplayText size='large' weight='Medium'>
                        <div className='fx '>
                          <span className='w60 ta-r'>
                            <TextStyle variation='subdued'>
                              Credits Applied
                            </TextStyle>
                          </span>
                          <span className='w40 tend'>
                            <TextStyle variation='subdued'>
                              {locale &&
                                currency &&
                                tenure &&
                                new Intl.NumberFormat(locale, {
                                  style: 'currency',
                                  currency,
                                  minimumFractionDigits: 2,
                                  currencyDisplay: 'narrowSymbol'
                                }).format(invoiceState.creditsApplied)}
                            </TextStyle>
                          </span>
                        </div>
                      </DisplayText>
                    </div>
                  )}
                  <div className='mb-5'>
                    <DisplayText size='extraLarge' weight='Medium'>
                      <div className='fx '>
                        <span className='w60 ta-r'>
                          <TextStyle weight='Bold' variation='positive'>
                            <FormattedMessage id={'invoice.details.payNow'} />
                          </TextStyle>
                        </span>
                        <span className='w40 tend'>
                          <TextStyle weight='Bold' variation='positive'>
                            {locale &&
                              currency &&
                              tenure &&
                              new Intl.NumberFormat(locale, {
                                style: 'currency',
                                currency,
                                minimumFractionDigits: 2,
                                currencyDisplay: 'narrowSymbol'
                              }).format(invoiceState?.amountDue)}
                          </TextStyle>
                        </span>
                      </div>
                    </DisplayText>
                  </div>
                </div>
              )}
              {invoiceState && (
                <div className='flex jend plr-20'>
                  <ButtonGroup>
                    <Button
                      id='pricingContactSupport'
                      plain
                      onClick={() => window.Intercom('show')}
                    >
                      <FormattedMessage id='app.pricing.contactsupport' />
                    </Button>

                    <Button
                      id='pricingPaymentButton'
                      primary
                      disabled={!isEmpty(countErrors) || isEstimating}
                      onClick={openCheckout}
                    >
                      <FormattedMessage id='app.pricing.payment.button' />
                    </Button>
                  </ButtonGroup>
                </div>
              )}
              {flags?.itOverageBilling &&
                overageEstimate?.lineItemGroups?.map((invoiceSplit, i) => {
                  return (
                    <ItemGroupList
                      deviceCount={deviceCount}
                      invoiceState={invoiceSplit}
                      cumulatedInvoice={overageEstimate}
                      isOverageEstimate
                      openSummary={isEmpty(invoiceState)}
                      {...props}
                    />
                  )
                })}

              {nextinvoiceState?.lineItemGroups?.map((invoiceSplit, i) => {
                return (
                  <ItemGroupList
                    deviceCount={deviceCount}
                    invoiceState={invoiceSplit}
                    cumulatedInvoice={nextinvoiceState}
                    nextTermInvoice
                    openSummary={
                      isEmpty(overageEstimate) && isEmpty(invoiceState)
                    }
                    {...props}
                  />
                )
              })}
            </>
          )}
        </div>
      </div>
      <div className='p20 pt40 mt20'>
        {flags?.itOverageBilling && overAgeDevices !== 'NA' && (
          <div className='mb24'>
            <Banner status='warning'>
              <div className=''>
                <div className='fx actr g5'>
                  <DisplayText weight='Bold' size='large'>
                    <FormattedMessage
                      id={'itsm.pricing.overage.detected.label'}
                    />
                  </DisplayText>
                </div>
                <div className='mt10'>
                  <DisplayText size='large'>
                    <FormattedMessage
                      id={
                        isPrimePlus
                          ? 'itsm.app.pricing.overage.info.points.prime.plus'
                          : 'itsm.app.pricing.overage.info.points'
                      }
                      values={{
                        br: <br />,
                        ul: (chunks) => <ul>{chunks}</ul>,
                        li: (chunks) => <li>{chunks}</li>,
                        b: (chunks) => <b>{chunks}</b>
                      }}
                    />
                  </DisplayText>
                </div>
              </div>
            </Banner>
          </div>
        )}
        <Card border='secondary' padding='sizeSmall'>
          <div className=''>
            <div className='fx actr g5'>
              <SVGIcon name='About' />
              <DisplayText weight='Bold' size='large'>
                <FormattedMessage id={'common.label.information'} />
              </DisplayText>
            </div>
            <div className='mt10'>
              <DisplayText size='large'>
                <FormattedMessage
                  id={
                    isPrimePlus || isTieredModel
                      ? 'itsm.app.pricing.info.points.prime.plus'
                      : 'itsm.app.pricing.info.points'
                  }
                  values={{
                    br: <br />,
                    ul: (chunks) => <ul>{chunks}</ul>,
                    li: (chunks) => <li>{chunks}</li>,
                    b: (chunks) => <b>{chunks}</b>
                  }}
                />
              </DisplayText>
            </div>
          </div>
        </Card>
      </div>
    </ContainerWrapper>
  )
}

export default withRouter(ITPricingEstimateContainer)
